import { computed, defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";

import { ApplicationEnum, AxxessCareDisciplineEnum } from "../../../enums/enums";
import {
    IPatientDetailsAxxessCare,
    IPostAxxessCareResponse,
    ISpecialtyAxessCare,
    IVisitDetails
} from "../../../resources-vue/vue-interfaces/i-visit-details";
import { PostToAxessCareDetails } from "../../../models/task.model";
import { TaskService } from "../../../services/task.service";
import { ToastrService } from "../../../services/toastr.service";
import { formatDate } from "./../../../common/vue-helpers/modifiers/value-modifier";

import AddressModal from "../AddressModal/AddressModal.vue";
import PayrateModal from "../PayrateModal/PayrateModal.vue";

export default defineComponent({
    inject: ["dialogRef"],
    data() {
        return {
            dialogReference: this.dialogRef as any,
            options: {} as IVisitDetails,
            applicationEnum: ApplicationEnum,
            isLoading: false,
            isSaving: false,
            _taskService: null,
            _toastrService: null,
            dialogRef: null,
            patientAddressDetailsAxxessCare: {} as IPatientDetailsAxxessCare,
            postToAxxessCareDetails: {} as PostToAxessCareDetails,
            axxessCareSpecialities: [] as ISpecialtyAxessCare[],
            formatDate
        };
    },

    created() {
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },
    async mounted() {
        this.postToAxxessCareDetails.SpecialtyId = null;
        this.options = Object.assign({}, this.dialogReference.data.visitDetails);
        const specialties: Promise<ISpecialtyAxessCare[]> = await this._taskService.getAxxessCareSpecialties({
            agencyId: this.options.AgencyId,
            application: this.options.Application,
            // in homehealth enum values are different from axxess care by 1 so using mapper here as the enum value of discipline
            discipline:
                ApplicationEnum.AgencyCore === this.options.Application
                    ? this.toAxxessCareDisciplineMapper(this.options.Discipline)
                    : this.options.Discipline,
            disciplineDescription:
                ApplicationEnum.AgencyCore === this.options.Application ? this.options.DisciplineDesc : ""
        });
        let details: Promise<IPatientDetailsAxxessCare>;
        let specialities: ISpecialtyAxessCare[];

        if (this.options.AgencyId && this.options.Id) {
            details = this._taskService.getAxxessCareDetails(this.options.PatientId, {
                agencyId: this.options.AgencyId,
                discipline: this.options.Discipline,
                disciplineTask: this.options.DisciplineTask,
                application: this.options.Application,
                episodeId: this.options.EpisodeId,
                taskId: this.options.Id
            });
        }

        try {
            [specialities, this.patientAddressDetailsAxxessCare] = await Promise.all([specialties, details]);
            this.assignValues(specialities);
        } catch (error) {
            this._toastrService.error({
                title: `Error - Posting to AxessCare`,
                message: `There was a problem while posting this task to AxxessCare</b>. Please try again.`
            });
            this.cancel();
        } finally {
            this.isLoading = false;
        }
    },

    methods: {
        cancel() {
            this.dialogReference.close();
        },
        ok() {
            this.dialogReference.close("success");
        },
        assignValues(specialities: ISpecialtyAxessCare[] = []) {
            if (specialities.length && this.options.Discipline) {
                this.axxessCareSpecialities = specialities.filter(
                    (speciality: ISpecialtyAxessCare) =>
                        speciality.disciplineId ==
                        (ApplicationEnum.AgencyCore === this.options.Application
                            ? this.toAxxessCareDisciplineMapper(this.options.Discipline)
                            : this.options.Discipline)
                );
            }
            Object.assign(
                this.postToAxxessCareDetails,
                JSON.parse(JSON.stringify(this.patientAddressDetailsAxxessCare))
            );
            this.postToAxxessCareDetails.AgencyId = this.options.AgencyId;
            this.postToAxxessCareDetails.EpisodeId = this.options.EpisodeId;
            this.postToAxxessCareDetails.PatientId = this.options.PatientId;
            this.postToAxxessCareDetails.EventId = this.options.Id;
            this.postToAxxessCareDetails.TaskType = this.options.TaskName;
            this.postToAxxessCareDetails.ScheduleDate = this.options.StartDate;
        },

        async postToAxxessCare() {
            if (!this.postToAxxessCareDetails.SpecialtyId) {
                this._toastrService.error({
                    title: `Error - Posting to AxessCare`,
                    message: `You must select task before posting to AxxessCARE`
                });
                return;
            } else {
                let result: IPostAxxessCareResponse;
                if (this.options.Application === ApplicationEnum.HomeCare) {
                    const visitDates: any = [this.options.StartDate];
                    let specialityName = this.axxessCareSpecialities.find(
                        (task: any) => task.id === this.postToAxxessCareDetails.SpecialtyId
                    ).name;
                    let payload = visitDates.map((date: any) => {
                        this.postToAxxessCareDetails.AddressLine2 = this.patientAddressDetailsAxxessCare.AddressLine2;
                        return {
                            UserId: this.options.UserId,
                            Languages: "English",
                            CustomId: this.options.customId,
                            AgencyId: this.postToAxxessCareDetails.AgencyId,
                            EpisodeId: this.postToAxxessCareDetails.EpisodeId,
                            PatientId: this.postToAxxessCareDetails.PatientId,
                            EventId: this.postToAxxessCareDetails.EventId,
                            DisciplineTaskId: this.options.DisciplineTaskId,
                            VisitType: this.options.visitType,
                            ScheduleDate: `${moment(date).format("YYYY-MM-DD")}T00:00:00.000Z`,
                            TaskType: this.postToAxxessCareDetails.SpecialtyId,
                            SpecialtyId: this.postToAxxessCareDetails.SpecialtyId,
                            PrivateJob: this.postToAxxessCareDetails.PrivateJob,
                            AddressLine1: this.postToAxxessCareDetails.AddressLine1,
                            AddressLine2: this.postToAxxessCareDetails.AddressLine2,
                            StateCode: this.postToAxxessCareDetails.StateCode,
                            City: this.postToAxxessCareDetails.City,
                            ZipCode: this.postToAxxessCareDetails.ZipCode,
                            VisitDetails: this.postToAxxessCareDetails.VisitDetails,
                            HipaaInfo: this.postToAxxessCareDetails.HipaaInfo,
                            PayRates: this.postToAxxessCareDetails?.PayRates,
                            StartDate: `${moment(date).format("YYYY-MM-DD")}T00:00:00.000Z`,
                            EndDate: `${moment(date).format("YYYY-MM-DD")}T00:00:00.000Z`,
                            SpecialityName: specialityName,
                            PostalCode: this.postToAxxessCareDetails.PostalCode,
                            ActivePayInformationType: this.postToAxxessCareDetails.ActivePayInformationType
                        };
                    });
                    result = await this._taskService.multiplePostToAxxessCare({
                        CareVisits: payload,
                        CurrentAgencyId: this.postToAxxessCareDetails.AgencyId,
                        CurrentUserId: this.options.UserId,
                        isPostVisitInternally: this.postToAxxessCareDetails.PrivateJob,
                        Application: this.options.Application
                    });
                } else {
                    result = await this._taskService.postTaskToAxxessCare(this.postToAxxessCareDetails);
                }
                if (!result || !result.isSuccessful) {
                    this._toastrService.error({
                        title: `Error - Posting to AxessCare`,
                        message: `There was a problem while posting this task to AxxessCare</b>. Please try again.`
                    });
                    this.cancel();
                } else {
                    this._toastrService.success({
                        title: `Posted to AxxessCare`,
                        message: `The task was posted successfully to AxxessCare`
                    });
                    this.ok();
                }
            }
        },

        async editAddress() {
            this.dialogRef = this.$dialog.open(AddressModal, {
                props: {
                    modal: true,
                    contentStyle: {
                        width: "400px",
                        overflowX: "hidden"
                    },
                    showHeader: false
                },
                data: {
                    address: this.patientAddressDetailsAxxessCare
                },
                onClose: (response) => {
                    if (response.data.Message == "success") {
                        Object.assign(this.postToAxxessCareDetails, response?.data);
                    }
                }
            });
        },

        toAxxessCareDisciplineMapper(val: number) {
            if (val !== AxxessCareDisciplineEnum.Nursing) {
                return val - 1;
            }
            return val;
        },

        async editPayRate() {
            this.dialogRef = this.$dialog.open(PayrateModal, {
                props: {
                    modal: true,
                    contentStyle: {
                        width: "400px",
                        overflowX: "hidden"
                    },
                    showHeader: false
                },
                data: {
                    payRates: this.patientAddressDetailsAxxessCare?.PayRates
                },
                onClose: (response) => {
                    if (response.data.Message == "success") {
                        Object.assign(this.postToAxxessCareDetails, response?.data);
                    }
                }
            });
        }
    }
});
